import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Users, Ticket, CheckCircle2, Clock, TrendingUp,
  Search, RefreshCw, Car, CalendarDays
} from 'lucide-react';
import { dashboardAPI } from '../services/api';
import { format } from 'date-fns';
import toast from 'react-hot-toast';

const StatCard = ({ icon: Icon, label, value, sub, color }) => (
  <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
      <Icon className="w-6 h-6 text-white" />
    </div>
    <div>
      <div className="text-2xl font-bold text-gray-900 dark:text-white tabular-nums">
        {typeof value === 'number' ? value.toLocaleString() : value}
      </div>
      <div className="text-sm text-gray-500 dark:text-gray-400">{label}</div>
      {sub && <div className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">{sub}</div>}
    </div>
  </div>
);

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const fetchStats = async () => {
    setLoading(true);
    try {
      const res = await dashboardAPI.getStats();
      setStats(res.data.data);
    } catch {
      toast.error('Failed to load dashboard stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchStats(); }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const claimPct = stats?.totalPossible
    ? Math.round((stats.totalClaimed / stats.totalPossible) * 100)
    : 0;

  return (
    <div className="max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Coupon service overview — {format(new Date(), 'EEEE, d MMMM yyyy')}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            onClick={fetchStats}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-gray-600 dark:text-gray-400"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh
          </button>
          <button
            onClick={() => navigate('/search')}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-brand-600 hover:bg-brand-700 text-white rounded-lg transition-colors"
          >
            <Search className="w-4 h-4" />
            Search Customer
          </button>
        </div>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          label="Total Customers"
          value={stats?.totalCustomers ?? 0}
          color="bg-blue-500"
        />
        <StatCard
          icon={Ticket}
          label="Total Coupons"
          value={stats?.totalCoupons ?? 0}
          color="bg-violet-500"
        />
        <StatCard
          icon={CheckCircle2}
          label="Services Claimed"
          value={stats?.totalClaimed ?? 0}
          sub={`${claimPct}% of total`}
          color="bg-emerald-500"
        />
        <StatCard
          icon={Clock}
          label="Services Remaining"
          value={stats?.totalRemaining ?? 0}
          color="bg-amber-500"
        />
      </div>

      {/* Progress bar */}
      <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <TrendingUp className="w-4 h-4 text-brand-500" />
            Overall Service Utilization
          </div>
          <span className="text-sm font-bold text-brand-600 dark:text-brand-400">{claimPct}%</span>
        </div>
        <div className="h-2.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-brand-500 to-brand-600 rounded-full transition-all duration-700"
            style={{ width: `${claimPct}%` }}
          />
        </div>
        <div className="flex justify-between text-xs text-gray-400 dark:text-gray-500 mt-2">
          <span>{stats?.totalClaimed?.toLocaleString()} claimed</span>
          <span>{stats?.totalPossible?.toLocaleString()} total</span>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Coupon type breakdown */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <Car className="w-4 h-4 text-brand-500" />
            Coupon Type Breakdown
          </h2>
          {stats?.couponBreakdown?.length > 0 ? (
            <div className="space-y-3">
              {stats.couponBreakdown.map((item, i) => {
                const colors = ['bg-brand-500', 'bg-violet-500', 'bg-emerald-500'];
                const pct = Math.round((item.count / stats.totalCustomers) * 100);
                return (
                  <div key={i}>
                    <div className="flex justify-between text-sm mb-1">
                      <span className="text-gray-600 dark:text-gray-400">{item.coupon_type || 'Unknown'}</span>
                      <span className="font-medium text-gray-900 dark:text-white">{parseInt(item.count).toLocaleString()}</span>
                    </div>
                    <div className="h-1.5 bg-gray-100 dark:bg-gray-800 rounded-full overflow-hidden">
                      <div className={`h-full ${colors[i % colors.length]} rounded-full`} style={{ width: `${pct}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">No data available</p>
          )}
        </div>

        {/* Recent activity */}
        <div className="bg-white dark:bg-gray-900 rounded-xl border border-gray-200 dark:border-gray-800 p-5">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <CalendarDays className="w-4 h-4 text-brand-500" />
            Recent Claimed Services
          </h2>
          {stats?.recentActivity?.length > 0 ? (
            <div className="space-y-2.5 max-h-64 overflow-y-auto">
              {stats.recentActivity.map((item, i) => (
                <div key={i} className="flex items-start gap-3 py-2 border-b border-gray-100 dark:border-gray-800 last:border-0">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 flex-shrink-0" />
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900 dark:text-white truncate">{item.customer_name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 truncate">{item.service_name}</div>
                    <div className="text-xs text-gray-400 dark:text-gray-500">
                      {item.service_claim_date ? format(new Date(item.service_claim_date), 'dd MMM yyyy, hh:mm a') : '—'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-gray-400 dark:text-gray-500">No recent activity</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
